import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { useAuthentication } from '@src/authentication/useAuthentication';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { DocumentTable } from '@src/documents/components/DocumentTable';
import { useFindAllDocumentsForOrder } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import { useShowDocumentToClient } from '@src/documents/hooks/useShowDocumentToClient';
import { useFindAllNotesForOrder } from '@src/notes/hooks/useFindAllNotesForOrder';
import { OrderDetails } from '@src/orders/components/OrderDetails';
import { OrderNotes } from '@src/orders/components/OrderNotes';
import { CountBadge } from '@src/orders/CountBadge';
import type { OrderListing } from '@src/orders/domain/OrderList';
import { OrderDocumentUploader } from '@src/orders/OrderDocumentUploader';
import { OrderReviewLoader } from '@src/orders/review/OrderReviewLoader';
import { TabPanel } from '@src/orders/TabPanel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  currentOrder: OrderListing;
  mode: string;
  onDeleteSuccess: () => void;
  onOrderChange: () => void;
  onPermitChange: (order: OrderListing) => void;
  highlightedPermit?: string;
}

export const OrderDrawerContent = ({
  currentOrder,
  mode,
  onDeleteSuccess,
  onOrderChange,
  onPermitChange,
  highlightedPermit,
}: Props) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const { user } = useAuthentication();
  const { notes } = useFindAllNotesForOrder({
    orderId: currentOrder.id,
  });
  const { markToShowToClient } = useShowDocumentToClient({
    orderId: currentOrder.id,
  });

  const { documents, refetch: refetchDocuments } = useFindAllDocumentsForOrder(
    currentOrder.id,
  );
  const tabProps = (tabNumber: number) => {
    return {
      id: `horizontal-tab-${tabNumber}`,
      'aria-controls': `horizontal-tabpanel-${tabNumber}`,
    };
  };

  const onCheck = ({ id, checked }: { id?: string; checked: boolean }) => {
    if (!id) {
      return;
    }
    markToShowToClient({
      documentId: id,
      showToClient: checked,
    });
  };

  const visibleTabs = [
    {
      key: 'details',
      label: t('orderDetails'),
      content: (
        <>
          {currentOrder && mode === 'summary' && (
            <OrderDetails
              order={currentOrder}
              onDeleteSuccess={onDeleteSuccess}
              onOrderChange={onOrderChange}
              onPermitChange={onPermitChange}
              highlightedPermit={highlightedPermit}
            />
          )}
          {currentOrder && (mode === 'review' || mode === 'viewInvoice') && (
            <OrderReviewLoader orderId={currentOrder.id} />
          )}
        </>
      ),
    },
    {
      key: 'notes',
      label: (
        <Stack direction="row" gap={theme.spacing[2]} alignItems="center">
          {t('notes.label')}
          {notes.length > 0 && <CountBadge count={notes.length} />}
        </Stack>
      ),
      content: <OrderNotes orderId={currentOrder.id} />,
    },
    ...[
      {
        key: 'documents',
        label: (
          <Stack direction="row" gap={theme.spacing[2]} alignItems="center">
            {t('documents.label')}
            {documents.length > 0 && <CountBadge count={documents.length} />}
          </Stack>
        ),
        content: (
          <Stack gap={theme.spacing[8]}>
            <Typography variant="subtitle1">
              {t('documents.title', { orderId: currentOrder.displayId })}
            </Typography>
            <OrderDocumentUploader
              orderId={currentOrder.id}
              onUploadSuccess={refetchDocuments}
            />
            <DocumentTable
              documents={documents}
              readonly={isClientUser(user)}
              onCheck={onCheck}
              defaultChecked={documents
                .filter((doc) => doc.showToClient || doc.sendToClient)
                .map((doc) => doc.id)}
              readonlyChecked={documents
                .filter((doc) => doc.sendToClient)
                .map((doc) => doc.id)}
            />
          </Stack>
        ),
      },
    ],
  ];

  return (
    <>
      <MuiTabs
        value={currentTab}
        onChange={(_, index) => setCurrentTab(index)}
        variant="standard"
        sx={{ marginBottom: 4 }}
        TabIndicatorProps={{ style: { height: '1px' } }}
      >
        {visibleTabs.map((tab, index) => (
          <MuiTab
            key={tab.key}
            label={tab.label}
            className={TabStyle}
            {...tabProps(index)}
          />
        ))}
      </MuiTabs>

      <Stack>
        {visibleTabs.map((tab, index) => (
          <TabPanel key={tab.key} index={index} value={currentTab}>
            {tab.content}
          </TabPanel>
        ))}
      </Stack>
    </>
  );
};

const TabStyle = css(() => ({
  width: '165px',
  maxWidth: '165px',
}));
